// React
import { useEffect, useState } from 'react';

// Shared components
import { PreviewSheet } from '../../PreviewSheet';

// Local components
import { ResaleItemOverviewSection } from './ResaleItemOverviewSection';
import { ResaleItemStatusDropdown } from './ResaleItemStatusDropdown';

// Icons
import { ExternalLinkIcon } from '@icons/ExternalLinkIcon';

// Hooks
import { useTranslation } from '@hooks/useTranslation';

// Types
import type { ResaleItem } from '../types';
import { FooterEditLink } from '../../common/FooterEditLink';

interface ResaleItemPreviewDrawerProps {
  resaleItem: ResaleItem | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** RBAC: when false, hide write affordances (status toggle + edit). */
  canWrite?: boolean;
}

function DrawerHeader({ resaleItem, onClose, canWrite }: { resaleItem: ResaleItem; onClose: () => void; canWrite: boolean }) {
  const { t } = useTranslation();
  const label = t('preview_drawer.header.view_item_details');

  return (
    <div className="flex w-full items-center justify-between">
      <div className="flex items-center gap-3">
        <a
          href={`/resale_items/${resaleItem.id}/details`}
          onClick={onClose}
          className="text-grey transition-colors hover:text-off-black"
          aria-label={label}
        >
          <ExternalLinkIcon />
        </a>
        <span className="font-inter text-lg font-semibold text-off-black">
          {label}
        </span>
      </div>
      {canWrite && (
        <ResaleItemStatusDropdown resaleItemId={resaleItem.id} active={resaleItem.active} />
      )}
    </div>
  );
}

export function ResaleItemPreviewDrawer({
  resaleItem,
  open,
  onOpenChange,
  canWrite = true,
}: ResaleItemPreviewDrawerProps) {
  const { t } = useTranslation();
  // Keep the last non-null item so the drawer stays populated during Radix's
  // close animation (parent resets to null on close).
  const [stableItem, setStableItem] = useState<ResaleItem | null>(resaleItem);
  useEffect(() => {
    if (resaleItem !== null) setStableItem(resaleItem);
  }, [resaleItem]);
  const activeItem = resaleItem ?? stableItem;

  return (
    <PreviewSheet
      open={open && activeItem !== null}
      onOpenChange={onOpenChange}
      header={
        activeItem ? (
          <DrawerHeader resaleItem={activeItem} onClose={() => onOpenChange(false)} canWrite={canWrite} />
        ) : undefined
      }
      footerClassName="justify-end"
      footer={activeItem && canWrite ? <FooterEditLink href={`/resale_items/${activeItem.id}/edit`} label={t('preview_drawer.footer.edit')} /> : undefined}
    >
      {activeItem ? <ResaleItemOverviewSection resaleItem={activeItem} /> : null}
    </PreviewSheet>
  );
}
